import { z } from "zod";
import { paginationResponseType } from "@/types";

// ============ TIPOS COMUNS ============
export type UserRole = 'admin' | 'client' | 'seller' | 'accountant' | 'inactive' | 'canceled';
export type UserRoleResponse = 'ADMIN' | 'CLIENT' | 'SELLER' | 'ACCOUNTANT' | 'INACTIVE' | 'CANCELED';

// ============ CREATE USER ============
export type UserState = {
    success?: boolean;
    message?: string;
    errors?: {
        hits?: string[];
        code?: string[];
        identifier?: string[];
        name?: string[];
        userName?: string[];
        email?: string[];
        password?: string[];
        observation?: string[];
        userType?: string[];
    };
    values?: {
        hits: number;
        code: string;
        identifier: string;
        name: string;
        userName: string;
        email: string;
        password: string;
        observation: string;
        userType: string;
    };
};

export type UserType = {
    hits: number;
    code: string;
    identifier: string;
    name: string;
    userName: string;
    email: string;
    password: string;
    observation: string | undefined;
    userType: string;
};

export const userSchema = z.object({
    hits: z.number().default(0),
    code: z.string()
        .min(4, { message: "Código deve ter pelo menos 4 caracteres numéricos" })
        .max(4, { message: "Código não pode exceder 4 caracteres numéricos" }),
    identifier: z.string()
        .min(11, { message: "CPF/CNPJ deve ter pelo menos 11 caracteres" })
        .max(14, { message: "CPF/CNPJ não pode exceder 14 caracteres" }),
    name: z.string()
        .min(2, { message: "Nome deve ter pelo menos 2 caracteres" })
        .max(100, { message: "Nome não pode exceder 100 caracteres" }),
    userName: z.string().optional(),
    email: z.email({ message: "Por favor, insira um e-mail válido" }),
    password: z.string().min(6, { message: "Senha deve ter pelo menos 6 caracteres" }),
    observation: z.string().optional(),
    userType: z.enum(['admin', 'client', 'seller', 'accountant'])
});

// ============ UPDATE ALL USERS (self) ============
export type SelfUserResponseType = {
    identifier: string;
    createdAt: string;
    email: string;
    name: string;
};

export const defaultSelfUser: SelfUserResponseType = {
    identifier: '',
    createdAt: '',
    email: '',
    name: '',
};

export type UpdateSelfUserType = {
    name: string;
    email: string;
    password: string;
};

export type updateSelfUserStateInfo = {
    success?: boolean;
    message?: string;
    errors?: {
        name?: string[];
        email?: string[];
        password?: string[];
        confirmPassword?: string[];
    };
    values?: {
        name: string;
        email: string;
        password: string;
        confirmPassword: string;
    };
};

export const updateSelfUserSchema = z.object({
    name: z.string()
        .min(2, { message: "Nome deve ter pelo menos 2 caracteres" })
        .max(100, { message: "Nome não pode exceder 100 caracteres" }),
    email: z.email({ message: "Por favor, insira um e-mail válido" }),
    password: z.string().min(6, { message: "Senha deve ter pelo menos 6 caracteres" }),
});

// ============ UPDATE USER (admin) ============
export type UpdateUserType = {
    id: string;
    code: string;
    identifier: string;
    name: string;
    userName: string;
    email: string;
    observation: string | undefined;
    userType: string;
};

export type UserIdResponseType = {
    id: string;
    code: string;
    identifier: string;
    createdAt: string;
    email: string;
    role: string;
    name: string;
    userName: string;
    observation: string | null;
};

export type UserStateInfo = {
    success?: boolean;
    message?: string;
    errors?: {
        id?: string[];
        code?: string[];
        identifier?: string[];
        name?: string[];
        userName?: string[];
        email?: string[];
        observation?: string[];
        userType?: string[];
    };
    values?: {
        id: string;
        code?: string;
        identifier: string;
        name: string;
        userName?: string;
        email: string;
        observation: string;
        userType: string;
    };
};

export const defaultUser: UserIdResponseType = {
    id: '',
    code: '',
    name: '',
    userName: '',
    email: '',
    observation: null,
    createdAt: '',
    identifier: '',
    role: '',
};

export const updateUserSchema = z.object({
    id: z.uuid(),
    code: z.string()
        .min(4, { message: "Código deve ter pelo menos 4 caracteres" })
        .max(4, { message: "Código não pode exceder 4 caracteres" }),
    identifier: z.string()
        .min(11, { message: "CPF/CNPJ deve ter pelo menos 11 caracteres" })
        .max(14, { message: "CPF/CNPJ não pode exceder 14 caracteres" }),
    name: z.string()
        .min(2, { message: "Nome deve ter pelo menos 2 caracteres" })
        .max(100, { message: "Nome não pode exceder 100 caracteres" }),
    userName: z.string().optional(),
    email: z.email({ message: "Por favor, insira um e-mail válido" }),
    observation: z.string().optional(),
    userType: z.enum(['admin', 'client', 'seller', 'accountant', 'inactive', 'canceled'])
});

// ============ UPDATE PASSWORD ============
export type UserPassState = {
    success?: boolean;
    message?: string;
    errors?: {
        id?: string[];
        password?: string[];
    };
    values?: {
        id: string;
        password: string;
    };
};

export const userPassSchema = z.object({
    id: z.uuid(),
    password: z.string().min(6),
});

export type UpdateUserPasswordType = {
    id: string;
    password: string;
};

// ============ GET USERS ============
export const searchUserSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(50).default(10),
    userType: z.enum(['admin', 'client', 'seller', 'accountant', 'inactive', 'canceled']).default('admin')
});

export type userResponseType = {
    id: string;
    hits: number;
    code: string;
    name: string;
    userName: string | null;
    email: string;
    observation: string | null;
    createdAt: string;
    password: string;
    role: string;
};

export type UsersResponse = {
    data: userResponseType[];
    pagination: paginationResponseType;
};

// ============ TIPOS PARA RESPONSES COMPLETOS ============
export type UserResponse = {
    id: string;
    hits: number;
    code: string;
    identifier: string;
    name: string;
    userName: string | null;
    email: string;
    observation: string | null;
    role: UserRoleResponse;
    createdAt: string;
    updatedAt: string;
};

export type UserAdminResponse = {
    id: string;
    hits: number;
    code: string;
    identifier: string;
    name: string;
    userName: string | null;
    email: string;
    observation: string | null;
    role: UserRoleResponse;
    createdAt: string;
    updatedAt: string;
};

// ============ TIPOS DE LOGS ============
export type UserLogType = {
    id: string;
    observation: string;
    createdAt: string;
};

export type UserLogsResponse = UserLogType[];

// ============ TIPOS DE USUARIOS PARA BUSCA ============
export type SearchUsersType = {
    id: string;
    code: string;
    userName: string;
    name: string;
};

export type SearchUsersResponse = {
    data: SearchUsersType[];
}

export interface SearchUserParams {
    query?: string;
    limit?: number;
}

export interface SearchUser {
    id: string;
    code: string;
    name?: string;
    userName: string;
}

// ============ TIPOS PARA FORMULÁRIOS ============
export type CreateUserFormData = {
    hits?: number;
    code: string;
    identifier: string;
    name: string;
    userName?: string;
    email: string;
    password: string;
    observation?: string;
    userType: UserRole;
};

export type UpdateUserFormData = {
    id: string;
    code?: string;
    identifier?: string;
    name?: string;
    userName?: string;
    email?: string;
    observation?: string;
    userType?: UserRole;
};